import { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Box, Typography, IconButton, TextField, InputAdornment,
  Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Snackbar, Alert, Button,
} from '@mui/material';
import { Refresh, Search, ArrowBack, LocalShipping } from '@mui/icons-material';

/* ── Tokens ── */
const C = {
  bg: '#f1f5f9',
  card: '#ffffff',
  border: '#e2e8f0',
  text: '#0f172a',
  sec: '#475569',
  muted: '#94a3b8',
  orange: '#ea580c',
  blue: '#1e40af',
  green: '#16a34a',
  red: '#dc2626',
};

const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
};

const todayStr = () => new Date().toISOString().split('T')[0];
/* Returns first day of current month as YYYY-MM-DD */
const firstDayOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const fmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/* ════════════════════════════════════════
   CASH ON DELIVERY LIST
════════════════════════════════════════ */
export default function CODList() {
  useSessionValidation();
  const history = useHistory();
  const { companyId, branchId, userId, token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(firstDayOfMonth());
  const [toDate, setToDate] = useState(todayStr());
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });

  const load = useCallback(async () => {
    if (!companyId || !token) return;
    setLoading(true);
    try {
      const res = await fetchData('ExecutivedashboardApi', `/MonthlyExchangesCODDriver/${companyId}/${userId}/${fromDate}/${toDate}`, token);
      const list = res?.response_data || res || [];
      setData(Array.isArray(list) ? list : []);
    } catch {
      setSnack({ open: true, msg: 'Failed to load cash on delivery list', sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId, userId, token, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter((r) =>
      (r.CustomerName || '').toLowerCase().includes(q) ||
      (r.BillNumber || '').toLowerCase().includes(q) ||
      (r.ContactNumber || '').includes(q)
    );
  }, [data, search]);

  const totalCOD = useMemo(
    () => filtered.reduce((s, r) => s + (parseFloat(r.ToCollect || r.BalanceAmount || r.CODAmount || r.TotalAmount) || 0), 0),
    [filtered]
  );

  if (loading) return <LogoLoader open message="Loading COD list..." />;

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', pb: 5 }}>

      {/* ── Header ── */}
      <Box sx={{
        bgcolor: '#fff', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1,
        borderBottom: '2px solid #ea580c', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
        <IconButton size="small" onClick={() => history.goBack()} sx={{ color: C.orange, p: 0.5 }}>
          <ArrowBack sx={{ fontSize: 18 }} />
        </IconButton>
        <LocalShipping sx={{ fontSize: 18, color: C.orange }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', flex: 1 }}>
          Cash On Delivery <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#475569' }}>COD Sale List</span>
        </Typography>
        <Chip label={`${filtered.length} records`} size="small"
          sx={{ bgcolor: '#fff7ed', color: C.orange, fontWeight: 700, fontSize: '0.7rem' }} />
        <IconButton size="small" onClick={load} sx={{ color: C.orange, p: 0.5 }}>
          <Refresh sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ px: 1.5, mt: 1.5 }}>

        {/* Summary strip */}
        {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: C.card, borderRadius: 2, px: 2, py: 1.25, mb: 1.5, border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <Box>
            <Typography sx={{ fontSize: '0.65rem', color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Total COD Pending</Typography>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: C.orange, lineHeight: 1.2 }}>{fmt(totalCOD)}</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '0.65rem', color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Bills</Typography>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: C.blue, lineHeight: 1.2 }}>{filtered.length}</Typography>
          </Box>
        </Box> */}

        {/* ── Single row: Search + From + To + Go ── */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search customer, bill no, contact..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 17, color: C.muted }} /></InputAdornment> }}
            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.82rem', bgcolor: C.card, '& fieldset': { borderColor: C.border } } }}
          />
          <TextField
            label="From" type="date" size="small" value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ max: toDate }}
            sx={{ width: 145, '& .MuiInputBase-input': { fontSize: '0.75rem', py: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.72rem' }, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: C.card, '& fieldset': { borderColor: C.border } } }}
          />
          <TextField
            label="To" type="date" size="small" value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ min: fromDate }}
            sx={{ width: 145, '& .MuiInputBase-input': { fontSize: '0.75rem', py: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.72rem' }, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: C.card, '& fieldset': { borderColor: C.border } } }}
          />
          <Button variant="contained" size="small" onClick={load}
            sx={{ bgcolor: C.orange, color: '#fff', fontWeight: 700, fontSize: '0.75rem', borderRadius: '10px', textTransform: 'none', px: 2, py: '6px', flexShrink: 0, '&:hover': { bgcolor: '#c2410c' } }}>
            Go
          </Button>
        </Box>

        <Box sx={{ bgcolor: C.card, borderRadius: 2.5, border: `1px solid ${C.border}`, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['#', 'Bill No', 'Bill Date', 'Customer', 'Scheduled To'].map((h) => (
                    <TableCell key={h} sx={{ bgcolor: '#fff7ed', fontWeight: 700, fontSize: '0.7rem', color: C.orange, textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', py: 1 }}>
                      {h}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ bgcolor: '#fff7ed', fontWeight: 700, fontSize: '0.7rem', color: C.orange, textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', py: 1 }}>
                    COD Amount
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#fff7ed', fontWeight: 700, fontSize: '0.7rem', color: C.orange, textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', py: 1 }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: C.muted, fontSize: '0.85rem' }}>
                      No cash on delivery records found
                    </TableCell>
                  </TableRow>
                ) : filtered.map((row, i) => {
                  const codAmt = parseFloat(row.ToCollect || row.CODAmount || 0);
                  const isCollected = row.IsDelivered || row.IsCODCollected || codAmt === 0;
                  return (
                    <TableRow key={row.Id ?? i} hover sx={{ '&:hover': { bgcolor: '#fff7ed' } }}>
                      <TableCell sx={{ fontSize: '0.72rem', color: C.muted }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, color: C.orange, whiteSpace: 'nowrap' }}>{row.BillNo || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{fmtDate(row.CreatedDate || row.CreatedDate)}</TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.CustomerName || '-'}
                        <p>{row.ContactNumber || row.CustomerContactNumber || '-'}</p>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', color: C.sec }}>{row.MasterName || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', fontWeight: 800, textAlign: 'right', whiteSpace: 'nowrap', color: isCollected ? C.green : C.red }}>
                        {fmt(codAmt)}
                      </TableCell>
                      {/* <TableCell>
                        <Chip label={isCollected ? 'Collected' : 'Pending'} size="small"
                          sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700,
                            bgcolor: isCollected ? '#dcfce7' : '#fef3c7',
                            color:   isCollected ? C.green : '#b45309' }} />
                      </TableCell> */}
                      <TableCell>
                        {(() => {
                          const exchStatus = row.IsReceived
                            ? "Completed"
                            : row.AllocatedDate
                              ? "Processing"
                              : "Pending";

                          return (
                            <Chip
                              label={exchStatus}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.62rem",
                                fontWeight: 700,
                                bgcolor:
                                  exchStatus === "Completed"
                                    ? "#dcfce7 !important"
                                    : exchStatus === "Processing"
                                      ? "#dbeafe !important"
                                      : "#fef9c3 !important",
                                color:
                                  exchStatus === "Completed"
                                    ? "#16a34a !important"
                                    : exchStatus === "Processing"
                                      ? C.blue
                                      : "#ca8a04 !important",
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(p => ({ ...p, open: false }))} sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
